"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useAuth = void 0;
var _defineProperty2 = _interopRequireDefault(require("@babel/runtime/helpers/defineProperty"));
var _client = require("@apollo/client");
var _lodash = _interopRequireDefault(require("lodash.omit"));
var _router = require("next/router");
var _react = require("react");
var _graphql = require("../generated/graphql");
var _auth = require("../utils/auth");
var _getStorageWithExpiry = _interopRequireDefault(require("../utils/getStorageWithExpiry"));
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { (0, _defineProperty2.default)(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
const isLoginRequested = authCode => typeof authCode === 'string';

/**
 * Get the auth status for the user.
 * The withAuth HOC extends this hook and allows you to render a loading state.
 */
const useAuth = options => {
  const router = (0, _router.useRouter)();
  const withAuthCode = isLoginRequested(router.query.authCode);
  const client = (0, _client.useApolloClient)();
  const {
    0: loading,
    1: setLoading
  } = (0, _react.useState)(false);
  const called = (0, _react.useRef)(false);
  const succesUrl = (0, _react.useMemo)(() => options?.onSucces?.(router), [options, router]);
  const failUrl = (0, _react.useMemo)(() => {
    if (typeof options?.onFail === 'function') {
      return options.onFail(router);
    }
    if (typeof options?.onFail === 'boolean' && options.onFail) {
      // when deeplinking lets start with a clean state
      const [nextPath] = router.asPath.split('?');
      return {
        pathname: `/login`,
        query: {
          next: `/${router.locale}/${nextPath}`
        }
      };
    }
    return undefined;
  }, [options, router]);
  const authQuery = options?.authQuery ?? _graphql.MeDocument;
  const result = (0, _react.useRef)();
  const fetch = (0, _react.useCallback)((opts = {}) =>
  // @ts-ignore
  client.query(_objectSpread({
    query: authQuery,
    fetchPolicy: 'cache-first',
    errorPolicy: 'ignore'
  }, opts)), [authQuery, client]);
  const removeAuthCode = (0, _react.useCallback)(async () => {
    await router.replace({
      pathname: router.pathname,
      query: (0, _lodash.default)(router.query, 'authCode')
    }, undefined, {
      shallow: true
    });
  }, [router]);
  const handleOnFail = (0, _react.useCallback)(async () => {
    if (failUrl === 'ignore') {
      setLoading(false);
      return;
    }
    if (failUrl) {
      await router.replace(failUrl);
    } else {
      setLoading(false);
    }
  }, [failUrl, router]);
  const handleMeCheck = (0, _react.useCallback)(async () => {
    const storageUtils = (0, _getStorageWithExpiry.default)('local');
    if (!storageUtils) {
      return;
    }
    called.current = true;
    if (withAuthCode) {
      const success = await (0, _auth.login)(router.query.authCode);
      if (success) {
        options?.onAfterLoginSuccess?.(router);
      }
      await removeAuthCode();
    }
    if (!storageUtils.getItem(_auth.ACCESS_TOKEN) && !storageUtils.getItem(_auth.REFRESH_TOKEN)) {
      if (!options?.ignoreMissingCredentials) {
        await handleOnFail();
      } else {
        setLoading(false);
      }
      return;
    }
    if (!storageUtils.getItem(_auth.ACCESS_TOKEN)) {
      const success = await (0, _auth.refresh)();
      if (!success) {
        await handleOnFail();
        return;
      }
    }
    result.current = await fetch();
    if (!result.current.data.viewer) {
      const success = await (0, _auth.refresh)();
      if (!success) {
        await handleOnFail();
        return;
      }
      result.current = await fetch({
        fetchPolicy: 'network-only'
      });
      if (!result.current.data.viewer) {
        await handleOnFail();
        return;
      }
    }
    if (result.current.data.viewer) {
      if (succesUrl) {
        await router.replace(succesUrl);
      } else {
        setLoading(false);
      }
    }
  }, [withAuthCode, fetch, router, removeAuthCode, options, handleOnFail, succesUrl]);
  (0, _react.useEffect)(() => {
    if (router.isReady && !called.current) {
      setLoading(true);
    }
  }, [router.isReady]);
  (0, _react.useEffect)(() => {
    if (loading && !called.current) {
      handleMeCheck();
    }
  }, [handleMeCheck, loading]);
  return {
    failUrl,
    showSpinner: loading || !called.current,
    isAuthorized: !!result.current?.data.viewer
  };
};
exports.useAuth = useAuth;